import { FormEvent, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Slide,
    Stack, TextField,
    Tooltip,
    Typography
} from "@mui/material";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { doApiPost } from "../../api/Requests";
import { authenticationStateHandler } from "../logic/AuthenticationStateHandler";
import { Address } from "../model/Address";
import AddressField from "./AddressField";
import { SimpleLink } from "../../components/SimpleLink";
import PasswordStrength, { passwordStrengthTest } from "./PasswordStrength"
import { AuthBox, LoginLinkStack, RoundIconButton, StepStack } from "./Authentication.styles";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../common/ui/LanguageSwitcher";
import PasswordRules from "./PasswordRules";
import { keyValueStorage } from "../../common/logic/KeyValueStore";
import { useLocale } from "../../hooks/UseLocale";
import { useAppDispatch } from "../../hooks/StoreHooks";
import { setFeatureFlagList } from "../../store/reducers/loginMetadataReducer";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AuthLayout from "./AuthLayout";
import { LoginSuccessfulResponse } from "../model/LoginSuccessfulResponse";
import { GeneralButton } from "../../common/ui/GeneralButton";
import {ErrorCode} from "../../api/errorCode";
import { UserManagementErrorCode } from "../../api/userManagementErrorCode";

interface RegisterState {
    email: string,
    emailRepeated: string,
    firstName: string,
    lastName: string,
    password: string,
    passwordRepeated: string,
    companyName: string,
    invoiceEmail: string,
    companyTaxId: string,
    companyPhoneNumber: string,
    companyAddress: Address,
}

/**
 * This screen provides a form for company admin users to register
 */
export function RegistrationScreen() {
    const [step, setStep] = useState<0 | 1>(0);
    const [showPassword, setShowPassword] = useState(false);
    const [showRepeatedPassword, setShowRepeatedPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0)
    const [errorMessage, setErrorMessage] = useState("");
    const locale = useLocale();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleClickShowRepeatedPassword = () => setShowRepeatedPassword((show) => !show);
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };
    const handleMouseDownRepeatedPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const [duplicateEmailError, setDuplicateEmailError] = useState(false);

    const [registerState, setRegisterState] = useState<RegisterState>({
        email: "",
        emailRepeated: "",
        firstName: "",
        lastName: "",
        password: "",
        passwordRepeated: "",
        companyName: "",
        invoiceEmail: "",
        companyTaxId: "",
        companyPhoneNumber: "",
        companyAddress: {
            street: "",
            number: "",
            postalCode: "",
            town: "",
            country: "",
            extraAddressLine: "",
        },
    });

    useEffect(() => {
        setPasswordStrength(passwordStrengthTest(registerState.password));
    }, [registerState.password]);

    const [isError, setIsError] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const nextButtonDisabled =
        !registerState.email ||
        !registerState.emailRepeated ||
        registerState.email !== registerState.emailRepeated;

    const handleRegisterSubmit = async function (event: FormEvent) {
        event.preventDefault()
        if (registerState.email !== registerState.emailRepeated) {
            setErrorMessage(t("authentication.register.emailMismatch"));
            setIsError(true);
            return;
        }
        if (registerState.password !== registerState.passwordRepeated) {
            setErrorMessage(t("authentication.register.passwordMismatch"));
            setIsError(true);
            return;
        }
        setIsLoading(true);
        setIsError(false);
        try {
            const result = await doApiPost<any, LoginSuccessfulResponse>(
                "/api/companymanagementuser",
                {
                    email: registerState.email,
                    password: registerState.password,
                    firstName: registerState.firstName,
                    lastName: registerState.lastName,
                    company: {
                        name: registerState.companyName,
                        invoiceEmail: registerState.invoiceEmail,
                        address: registerState.companyAddress,
                        phoneNumber: registerState.companyPhoneNumber,
                        taxId: registerState.companyTaxId
                    }
                },
                {
                    handleUnauthenticated: false,
                    credentials: "include"
                },
            );
            if (result.success()) {
                const { accessToken, csrfToken, role, features = [], supportedLanguages = [], assignedPermissions = [] } = result.result ?? {};
                const success = await authenticationStateHandler.refreshProfileAndSetAuthenticated(accessToken ?? null, csrfToken ?? null);
                if (success) {
                    keyValueStorage.setObject("userRole", role);
                    keyValueStorage.setObject("featureFlags", features);
                    keyValueStorage.setObject("supportedLanguages", supportedLanguages);
                    keyValueStorage.setObject("assignedPermissions", assignedPermissions);
                    dispatch(setFeatureFlagList(features));
                    if (features.includes("TestCompany")) {
                        window.location.href = `/${locale}/actionmodules`;
                        return
                    }
                    window.location.href = "/";
                    return;
                }
            } else {
                const errorResponse = result.errorResponse;
                const isDuplicateEmail = errorResponse?.errorCode === ErrorCode.ManagerUserCreationFailed &&
                    errorResponse.data?.errors?.some(
                        (e: { code: string }) => e.code === UserManagementErrorCode.DuplicateUserName
                    );

                const hasOtherErrors = errorResponse?.data?.errors?.some(
                    (e: { code: string }) => e.code !== UserManagementErrorCode.DuplicateUserName
                );

                if (isDuplicateEmail) {
                    setDuplicateEmailError(true);
                    setStep(0);
                }

                if (hasOtherErrors || (!isDuplicateEmail && errorResponse)) {
                    setErrorMessage(errorResponse?.message ?? t("label.unknownError"));
                    setIsError(true);
                }
            }
        } catch (error) {
            if (error instanceof Error) {
                setErrorMessage(error.message ?? t("label.unknownError"));
            } else {
                setErrorMessage(t("label.unknownError"));
            }
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }

    const missingFields = useMemo(() => {
        const fields: string[] = [];
        if (!registerState.email) fields.push(t("authentication.register.email"));
        if (!registerState.emailRepeated) fields.push(t("authentication.register.repeatEmail"));
        if (!registerState.firstName) fields.push(t("authentication.register.firstName"));
        if (!registerState.lastName) fields.push(t("authentication.register.lastName"));
        if (!registerState.password) fields.push(t("authentication.register.password"));
        if (!registerState.passwordRepeated) fields.push(t("authentication.register.repeatPassword"));
        if (!registerState.companyName) fields.push(t("authentication.register.companyName"));
        if (!registerState.invoiceEmail) fields.push(t("authentication.register.invoiceEmail"));
        const address = registerState.companyAddress;
        if (!address.street) fields.push(t("authentication.register.street"));
        if (!address.number) fields.push(t("authentication.register.number"));
        if (!address.postalCode) fields.push(t("authentication.register.postalCode"));
        if (!address.town) fields.push(t("authentication.register.town"));
        return fields;
    }, [registerState, t]);

    const isDisabled = missingFields.length > 0 || passwordStrength < 3;

    return (
        <AuthLayout>
            <AuthBox component="form" onSubmit={handleRegisterSubmit}>

                <Stack
                    direction="row"
                    sx={{ width: "100%", mb: 2, justifyContent: "space-between", alignItems: "center" }}
                >
                    <Typography variant="h5">
                        {t("authentication.register.register")} – {step === 0
                            ? t("authentication.register.personalData")
                            : t("authentication.register.companyData")}
                    </Typography>
                    <LanguageSwitcher />
                </Stack>

                {isError && <Alert severity="error" sx={{ width: "100%", mb: 2 }}>{errorMessage}</Alert>}

                <Box sx={{ position: "relative", width: "100%" }}>
                    <Slide
                        direction="right"
                        in={step === 0}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box sx={{ position: "relative" }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        required
                                        label={t("authentication.register.email")}
                                        value={registerState.email}
                                        type="email"
                                        error={duplicateEmailError}
                                        helperText={duplicateEmailError ? t("errorCode.UserEmailAlreadyTaken") : ""}
                                        onChange={(e) => {
                                            setDuplicateEmailError(false);
                                            setRegisterState({ ...registerState, email: e.target.value });
                                        }}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        required
                                        label={t("authentication.register.repeatEmail")}
                                        fullWidth
                                        value={registerState.emailRepeated}
                                        type="email"
                                        error={registerState.email !== registerState.emailRepeated && registerState.email !== ""}
                                        helperText={
                                            registerState.email !== registerState.emailRepeated && registerState.email !== ""
                                                ? t("authentication.register.emailMismatch")
                                                : ""
                                        }
                                        onChange={(v) =>
                                            setRegisterState({ ...registerState, emailRepeated: v.target.value })}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        required
                                        label={t("authentication.register.firstName")}
                                        value={registerState.firstName}
                                        onChange={(e) => setRegisterState({ ...registerState, firstName: e.target.value })}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        required
                                        label={t("authentication.register.lastName")}
                                        value={registerState.lastName}
                                        onChange={(e) => setRegisterState({ ...registerState, lastName: e.target.value })}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <OutlinedInput
                                        required
                                        fullWidth
                                        placeholder={t("authentication.register.password")}
                                        type={showPassword ? 'text' : 'password'}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                    onClick={handleClickShowPassword}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        value={registerState.password}
                                        onChange={(v) => {
                                            setRegisterState({ ...registerState, password: v.target.value })
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormControl
                                        fullWidth
                                        required
                                        error={registerState.password !== registerState.passwordRepeated}
                                    >
                                        <OutlinedInput
                                            placeholder={t("authentication.register.repeatPassword")}
                                            type={showRepeatedPassword ? "text" : "password"}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleClickShowRepeatedPassword}
                                                        onMouseDown={handleMouseDownRepeatedPassword}
                                                        edge="end"
                                                    >
                                                        {showRepeatedPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            value={registerState.passwordRepeated}
                                            onChange={(v) =>
                                                setRegisterState({
                                                    ...registerState,
                                                    passwordRepeated: v.target.value,
                                                })
                                            }
                                        />
                                        {registerState.password !== registerState.passwordRepeated && (
                                            <FormHelperText>
                                                {t("profilePage.passwordsDoNotMatch")}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <PasswordStrength strength={passwordStrength} />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <PasswordRules pw={registerState.password} />
                                </Grid>

                            </Grid>
                        </Box>
                    </Slide>

                    <Slide
                        direction="left"
                        in={step === 1}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box sx={{ position: "relative" }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        required
                                        label={t("authentication.register.companyName")}
                                        fullWidth
                                        value={registerState.companyName}
                                        onChange={(v) =>
                                            setRegisterState({ ...registerState, companyName: v.target.value })}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        required
                                        label={t("authentication.register.invoiceEmail")}
                                        fullWidth
                                        value={registerState.invoiceEmail}
                                        onChange={(v) =>
                                            setRegisterState({ ...registerState, invoiceEmail: v.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label={t("authentication.register.phoneNumber")}
                                        fullWidth
                                        value={registerState.companyPhoneNumber}
                                        onChange={(v) =>
                                            setRegisterState({ ...registerState, companyPhoneNumber: v.target.value })}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label={t("authentication.register.taxId")}
                                        fullWidth
                                        value={registerState.companyTaxId}
                                        onChange={(v) =>
                                            setRegisterState({ ...registerState, companyTaxId: v.target.value })}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <AddressField address={registerState.companyAddress}
                                        setAddress={
                                            (v) => setRegisterState({ ...registerState, companyAddress: v })
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Slide>
                </Box>

                <StepStack direction="row" spacing={2} sx={{ width: "100%", justifyContent: "center" }}>
                    {step === 1 && (
                        <Tooltip title={t("label.back")}>
                            <RoundIconButton
                                disabled={isLoading}
                                onClick={() => setStep(0)}>
                                <ArrowBackIcon />
                            </RoundIconButton>
                        </Tooltip>
                    )}
                    {step === 0 ? (
                        <Tooltip
                            title={
                                nextButtonDisabled
                                    ? t("authentication.register.emailMismatch")
                                    : t("label.next")
                            }
                        >
                            <span>
                                <RoundIconButton
                                    disabled={nextButtonDisabled}
                                    onClick={() => setStep(1)}
                                >
                                    <ArrowForwardIcon />
                                </RoundIconButton>
                            </span>
                        </Tooltip>
                    ) : (
                        <>
                            <Tooltip
                                title={
                                    isDisabled && missingFields.length > 0
                                        ? `${t("authentication.register.missingFields")}: ${missingFields.join(", ")}`
                                        : ""
                                }
                                arrow
                                placement="top"
                            >
                                <GeneralButton
                                    type="submit"
                                    fullWidth
                                    loading={isLoading}
                                    disabled={isDisabled}
                                >
                                    {t("authentication.register.register")}
                                </GeneralButton>
                            </Tooltip>
                        </>
                    )}
                </StepStack>
                <Divider flexItem sx={{ mb: 2 }} />
                <LoginLinkStack>
                    <Typography variant="body2">
                        <SimpleLink to={`/${locale}/login`}>
                            {t("authentication.register.alreadyRegistered")}
                        </SimpleLink>
                    </Typography>
                </LoginLinkStack>
            </AuthBox>
        </AuthLayout>
    )
}